/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import Breadcrumbs from '@atlaskit/breadcrumbs/breadcrumbs';
import { BreadcrumbsCurrentItem } from '@atlaskit/breadcrumbs/breadcrumbs-current-item';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs/breadcrumbs-item';
import { BreadcrumbsSkeleton } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton';
import { BreadcrumbsSkeletonItem } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton-item';
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { HUB_FEATURES, useIsHubFeatureEnabled } from '../../controllers/use-is-hub-feature-enabled';
import { useHubContext } from '../../utils';
import { getHubBreadcrumbHref } from '../../utils/get-hub-breadcrumb-href';
import { type HubBreadcrumbItem, useHubBreadcrumbItems } from './hub-breadcrumbs-context';
import { messages } from './messages';

const styles = cssMap({
	container: {
		boxSizing: 'border-box',
		minWidth: 0,
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		width: '100%',
	},
	item: {
		display: 'flex',
		boxSizing: 'border-box',
		minWidth: 0,
		maxWidth: '100%',
		alignItems: 'center',
		alignSelf: 'center',
		flexDirection: 'row',
		font: token('font.body'),
		marginTop: 0,
	},
	separator: {
		width: '8px',
		flexShrink: 0,
		paddingBlock: token('space.025'),
		paddingInline: token('space.100'),
		textAlign: 'center',
	},
	itemText: {
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const StaticHubParentBreadcrumb = ({ item }: { item: HubBreadcrumbItem }): JSX.Element => {
	return (
		<Box xcss={styles.item} as="li">
			<span css={styles.itemText}>
				<Text color={'color.text.subtlest'} maxLines={1}>
					{item.text}
				</Text>
			</span>
			<span aria-hidden css={styles.separator}>
				<Text color="color.text.subtlest">/</Text>
			</span>
		</Box>
	);
};

const StaticHubCurrentBreadcrumb = ({ item }: { item: HubBreadcrumbItem }): JSX.Element => {
	return (
		<Box xcss={styles.item} as="li">
			<span aria-current="page" css={styles.itemText}>
				<Text maxLines={1}>{item.text}</Text>
			</span>
		</Box>
	);
};

export const HubBreadcrumbsSkeleton = ({
	itemCount,
}: {
	itemCount: number;
}): JSX.Element | null => {
	const [isBreadcrumbsEnabled] = useIsHubFeatureEnabled([HUB_FEATURES.BREADCRUMBS]);

	if (!isBreadcrumbsEnabled) {
		return null;
	}

	return (
		<div css={styles.container}>
			<BreadcrumbsSkeleton>
				{Array.from({ length: itemCount }).map((_, index) => (
					<BreadcrumbsSkeletonItem width={60} key={index} />
				))}
			</BreadcrumbsSkeleton>
		</div>
	);
};

export const HubBreadcrumbs = (): JSX.Element | null => {
	const intl = useIntl();
	const items = useHubBreadcrumbItems();
	const currentItem = items[items.length - 1];
	const {
		env: { relativeBaseUrl },
	} = useHubContext();
	const { push } = useRouterActions();

	if (!currentItem) {
		return null;
	}

	const label = intl.formatMessage(messages.label);
	const ellipsisLabel = intl.formatMessage(messages.showMore);

	const onBreadcrumbItemClick = (href: string) => (event: React.MouseEvent) => {
		const isPrimaryClick = 'button' in event && event.button === 0;
		const isEnterKey = 'key' in event && event.key === 'Enter';
		if (
			!event.defaultPrevented &&
			(isPrimaryClick || isEnterKey) &&
			!event.metaKey &&
			!event.ctrlKey &&
			!event.altKey &&
			!event.shiftKey
		) {
			// The anchor needs the Hub base path, but browser history adds it on navigation.
			event.preventDefault();
			push(href);
		}
	};

	return (
		<div css={styles.container} data-testid="hub-breadcrumbs-container">
			<Breadcrumbs
				ellipsisLabel={ellipsisLabel}
				label={label}
				size="medium"
				testId="hub-breadcrumbs"
			>
				{items.slice(0, -1).map((item, index) =>
					item.href ? (
						<AnalyticsContext
							data={{ attributes: { itemType: item.id, position: index + 1 } }}
							key={item.id}
						>
							<BreadcrumbsItem
								href={getHubBreadcrumbHref(relativeBaseUrl, item.href)}
								onClick={onBreadcrumbItemClick(item.href)}
								text={item.text}
							/>
						</AnalyticsContext>
					) : (
						<StaticHubParentBreadcrumb item={item} key={item.id} />
					),
				)}
				{currentItem.href ? (
					<AnalyticsContext
						data={{ attributes: { itemType: currentItem.id, position: items.length } }}
					>
						<BreadcrumbsCurrentItem
							href={getHubBreadcrumbHref(relativeBaseUrl, currentItem.href)}
							text={currentItem.text}
						/>
					</AnalyticsContext>
				) : (
					<StaticHubCurrentBreadcrumb item={currentItem} />
				)}
			</Breadcrumbs>
		</div>
	);
};
